import type { ThemeConfig } from "antd";
import { theme as antdTheme } from "antd";

export type ThemeMode = "light" | "dark";

/** Single source of truth for both the antd theme and tokens.css.
 *  Modernist geometry (radius 0, 2px rules, Archivo) + Quattro blue. */
export const palette = {
  brand: "#17549a",
  brandHover: "#1f63ac",
  brandActive: "#103d70",
  brandLight: "#7ba7d8",
  danger: "#ec3013",
  warn: "#b3701a",
  ok: "#2f7d4f",
  gradient: "linear-gradient(150deg, #17549a, #221d1e)",
  light: {
    bg: "#f3f2f2",
    surface: "#ffffff",
    sunken: "#eae9e9",
    text: "#201e1d",
    textMuted: "#605d5d",
    border: "#d7d3d3",
  },
  dark: {
    bg: "#161514",
    surface: "#201e1d",
    sunken: "#1a1918",
    text: "#f3f2f2",
    textMuted: "#9b9797",
    border: "#3a3736",
  },
};

const FONT = '"Archivo", system-ui, -apple-system, "Segoe UI", sans-serif';

export function buildTheme(mode: ThemeMode): ThemeConfig {
  const p = mode === "dark" ? palette.dark : palette.light;
  const accent = mode === "dark" ? palette.brandLight : palette.brand;
  // signal colours lighten on a dark ground so they clear contrast against it
  const danger = mode === "dark" ? "#ff8a70" : palette.danger;
  const warn = mode === "dark" ? "#e0a54a" : palette.warn;
  const ok = mode === "dark" ? "#6cc08d" : palette.ok;

  return {
    algorithm: mode === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      // — color —
      colorPrimary: accent,
      colorInfo: accent,
      colorSuccess: ok,
      colorWarning: warn,
      colorError: danger,
      colorLink: accent,
      colorLinkHover: mode === "dark" ? "#b0cbea" : palette.brandHover,
      colorLinkActive: palette.brandActive,
      colorBgLayout: p.bg,
      colorBgContainer: p.surface,
      colorBgElevated: p.surface,
      colorBorder: p.border,
      colorBorderSecondary: p.border,
      colorText: p.text,
      colorTextSecondary: p.textMuted,
      colorTextTertiary: mode === "dark" ? "#7d7979" : "#7d7979",
      colorTextHeading: p.text,
      colorFillAlter: p.sunken,
      colorFillSecondary: p.sunken,

      // — geometry: Modernist is flat and square, everywhere —
      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,
      borderRadiusXS: 3,
      wireframe: false,

      // — typography scale —
      fontFamily: FONT,
      fontFamilyCode: 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace',
      fontSize: 13,
      fontSizeSM: 12,
      fontSizeLG: 15,
      fontSizeXL: 20,
      fontSizeHeading1: 32,
      fontSizeHeading2: 25,
      fontSizeHeading3: 20,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      lineHeight: 1.5,
      lineHeightHeading1: 1.12,
      lineHeightHeading2: 1.15,
      lineHeightHeading3: 1.2,

      // — density: compact, Linear-tight —
      controlHeight: 30,
      controlHeightSM: 24,
      controlHeightLG: 36,
      controlHeightXS: 20,
      padding: 16,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      sizeStep: 4,
      sizeUnit: 4,

      // — elevation: shadows are the exception, not the rule —
      boxShadow: mode === "dark" ? "0 1px 2px rgba(0,0,0,.5)" : "0 1px 2px rgba(45,43,43,.14)",
      boxShadowSecondary: mode === "dark" ? "0 3px 10px rgba(0,0,0,.55)" : "0 3px 10px rgba(45,43,43,.16)",
      boxShadowTertiary: "none",

      motionDurationMid: "0.12s",
      motionDurationSlow: "0.2s",
    },
    components: {
      Layout: {
        headerBg: p.surface,
        headerHeight: 48,
        headerPadding: "0 16px",
        bodyBg: p.bg,
        siderBg: "transparent",
        footerBg: p.bg,
      },
      Card: {
        headerBg: "transparent",
        headerHeight: 44,
        headerHeightSM: 36,
        headerFontSize: 14,
        headerFontSizeSM: 13,
        paddingLG: 16,
        boxShadowTertiary: "none",
        colorBorderSecondary: p.border,
      },
      Table: {
        headerBg: p.sunken,
        headerColor: p.textMuted,
        headerSplitColor: "transparent",
        borderColor: p.border,
        cellPaddingBlock: 8,
        cellPaddingInline: 12,
        cellPaddingBlockSM: 6,
        cellPaddingInlineSM: 8,
        rowHoverBg: mode === "dark" ? "#282625" : "#f8f4f4",
        rowSelectedBg: mode === "dark" ? "#0c2c4f" : "#eef4fb",
        rowSelectedHoverBg: mode === "dark" ? "#103d70" : "#d6e5f5",
        footerBg: p.sunken,
        fontWeightStrong: 600,
      },
      Button: {
        fontWeight: 600,
        primaryShadow: "none",
        defaultShadow: "none",
        dangerShadow: "none",
        paddingInline: 12,
        contentFontSize: 13,
        defaultBorderColor: p.border,
        defaultBg: p.surface,
        colorBgTextHover: mode === "dark" ? "#282625" : "#eae9e9",
      },
      Input: { paddingBlock: 4, paddingInline: 10, activeShadow: "none", hoverBorderColor: accent },
      Select: { optionSelectedBg: mode === "dark" ? "#0c2c4f" : "#eef4fb", optionHeight: 28, optionFontSize: 13 },
      Tabs: {
        horizontalMargin: "0 0 16px 0",
        horizontalItemPadding: "8px 0",
        horizontalItemGutter: 24,
        titleFontSize: 13,
        inkBarColor: accent,
        itemSelectedColor: p.text,
        itemHoverColor: accent,
      },
      Menu: {
        itemHeight: 32,
        itemMarginInline: 0,
        itemMarginBlock: 1,
        itemPaddingInline: 10,
        iconMarginInlineEnd: 10,
        iconSize: 15,
        collapsedIconSize: 16,
        darkItemBg: "transparent",
        darkSubMenuItemBg: "transparent",
        darkItemColor: "rgba(243,242,242,.72)",
        darkItemHoverBg: "rgba(255,255,255,.08)",
        darkItemHoverColor: "#ffffff",
        darkItemSelectedBg: "rgba(255,255,255,.12)",
        darkItemSelectedColor: "#ffffff",
        activeBarBorderWidth: 0,
      },
      Statistic: { titleFontSize: 11, contentFontSize: 25 },
      Tag: { defaultBg: p.sunken, defaultColor: p.textMuted, lineHeightSM: 1.6 },
      Descriptions: { labelBg: "transparent", itemPaddingBottom: 8, colonMarginRight: 12 },
      Modal: { titleFontSize: 16, headerBg: p.surface, contentBg: p.surface, paddingContentHorizontalLG: 20 },
      Segmented: { itemSelectedBg: p.surface, borderRadiusSM: 3 },
      Empty: { colorTextDescription: p.textMuted },
      Tooltip: { colorBgSpotlight: "#201e1d", borderRadius: 4 },
      Divider: { colorSplit: p.border },
      Form: { labelFontSize: 12, labelColor: p.textMuted, itemMarginBottom: 16, verticalLabelPadding: "0 0 4px" },
    },
  };
}
